import type { SchemaMap } from '@rachelallyson/spectra/catalog';
import type { Publisher } from '@rachelallyson/spectra/publishers';

/**
 * PostHog adapter. Takes a `capture(name, props?)` function instead of a
 * posthog client so each app brings its own: the frontend passes its
 * `utils/posthog.ts` capture, which carries the pre-init queue fix (posthog-js
 * drops events captured before init — the bug that recorded zero
 * welcomeAboardCheckoutStarted events for 9 days). Never wire posthog-js in
 * here directly.
 *
 * `renameMap` emits legacy PostHog names for events that predate the catalog,
 * so existing dashboards keep working until they're migrated (see
 * TURNSTILE_LEGACY_POSTHOG_NAMES).
 */
export function posthogCapturePublisher<TMap extends SchemaMap>(
  capture: (event: string, properties?: Record<string, unknown>) => void,
  options: {
    renameMap?: Readonly<Partial<Record<keyof TMap & string, string>>>;
  } = {}
): Publisher<TMap> {
  return {
    name: 'posthog',
    publish(event) {
      const name =
        options.renameMap?.[event.name as keyof TMap & string] ??
        String(event.name);
      const payload = event.payload as Record<string, unknown>;

      capture(name, Object.keys(payload).length > 0 ? payload : undefined);
    },
  };
}
